import React, { useState, useEffect, useRef } from 'react';
import { callAxiosGet } from '../tmslib/src/util/axiosutils';
import { useMessageState } from '../tmslib/src/context/MessageContext';
import { getMainAxios } from '../utils/util';
import { getApiConfig } from '../config/api';
import Highcharts from 'highcharts/highstock';
import HighchartsAccessibility from 'highcharts/modules/accessibility';
import HighchartsReact from 'highcharts-react-official';
import * as signalR from '@microsoft/signalr';

// Highcharts 접근성 모듈 초기화
HighchartsAccessibility(Highcharts);

interface RealTimePrice {
  prodId: string;
  currentPrice: number;
  openPrice: number;
  highPrice: number;
  lowPrice: number;
  volume: number;
  updateTime: string;
}

interface MinuteChartData {
  date: string;
  time: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  acmlVolume: number;
}

interface RealTimeDep {
  prodId: string;
  bsopHour: string;
  hourClsCode: string;
  askPrices: (number | null)[];
  bidPrices: (number | null)[];
  askQuantities: (number | null)[];
  bidQuantities: (number | null)[];
  totalAskQuantity: number;
  totalBidQuantity: number;
  ovtmTotalAskQuantity: number;
  ovtmTotalBidQuantity: number;
  antcCnpr: number;
  antcCnqn: number;
  antcVol: number;
  antcCntgVrss: number;
  antcCntgVrssSign: string;
  antcCntgPrdyCtrt: number;
  acmlVol: number;
  totalAskQuantityIcdc: number;
  totalBidQuantityIcdc: number;
  ovtmTotalAskIcdc: number;
  ovtmTotalBidIcdc: number;
  stckDealClsCode: string;
  updateTime: string;
}

interface AskingPriceData {
  askingPrice: {
    asprAcptHour: string;
    askPrices: string[];
    bidPrices: string[];
    askQuantities: string[];
    bidQuantities: string[];
    totalAskQuantity: string;
    totalBidQuantity: string;
  };
  expectedPrice: {
    currentPrice: number;
    openPrice: number;
    highPrice: number;
    lowPrice: number;
    standardPrice: number;
    expectedPrice: number;
    expectedPriceChangeSign: string;
    expectedPriceChange: string;
    expectedPriceChangeRate: string;
    expectedVolume: number;
  };
}

interface CcnlData {
  time: string;
  currentPrice: number;
  prdyVrss: string;
  prdyVrssSign: string;
  cntgVol: number;
  tdayRltv: string;
  prdyCtrt: string;
}

/** kwchartmin 분봉 d=YYYYMMDD, t=HHMMSS 를 KRX(한국 장) 로컬 시각으로 해석 → UTC epoch (Highcharts time.timezone 과 함께 사용). */
function krxBarToEpochMs(dateStr: string, timeStr: string): number {
  const y = dateStr.substring(0, 4);
  const mo = dateStr.substring(4, 6);
  const d = dateStr.substring(6, 8);
  const hh = timeStr.padStart(6, '0').substring(0, 2);
  const mm = timeStr.padStart(6, '0').substring(2, 4);
  const ss = timeStr.padStart(6, '0').substring(4, 6);
  return new Date(`${y}-${mo}-${d}T${hh}:${mm}:${ss}+09:00`).getTime();
}

/**
 * 연속 봉 사이 간격이 "정상 봉 간격"보다 크면 장중 비연속(야간·주말·공휴일 등)으로 보고 xAxis.breaks 생성.
 * 참고: https://www.highcharts.com/demo/stock/intraday-breaks
 */
function buildIntradayAxisBreaks(sortedTimestamps: number[], barStepMs: number): Highcharts.XAxisBreaksOptions[] {
  if (sortedTimestamps.length < 2) {
    return [];
  }
  const step = Math.max(barStepMs, 60_000);
  const gapThreshold = Math.max(step * 3, 8 * 60 * 1000);
  const breaks: Highcharts.XAxisBreaksOptions[] = [];
  for (let i = 1; i < sortedTimestamps.length; i++) {
    const gap = sortedTimestamps[i] - sortedTimestamps[i - 1];
    if (gap > gapThreshold) {
      breaks.push({
        from: sortedTimestamps[i - 1] + step,
        to: sortedTimestamps[i] - step,
      });
    }
  }
  return breaks;
}

const CurrentPrice: React.FC = () => {
  const [prodId, setProdId] = useState<string>('005930');
  const [realTimePrice, setRealTimePrice] = useState<RealTimePrice | null>(null);
  const [allPrices, setAllPrices] = useState<Record<string, RealTimePrice>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [isReceiving, setIsReceiving] = useState(false);
  const [receivingProdIds, setReceivingProdIds] = useState<string[]>([]);
  const [minuteChartData, setMinuteChartData] = useState<MinuteChartData[]>([]);
  const [dailyChartData, setDailyChartData] = useState<any[]>([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [minuteInterval, setMinuteInterval] = useState<number>(1);
  const [chartType, setChartType] = useState<'minute' | 'daily' | 'weekly'>('minute');
  /** 가격 축: 캔들 vs 종가 라인 */
  const [priceSeriesType, setPriceSeriesType] = useState<'candlestick' | 'line'>('candlestick');
  const [askingPriceData, setAskingPriceData] = useState<AskingPriceData | null>(null);
  const [ccnlData, setCcnlData] = useState<CcnlData[]>([]);
  const [depthLevel, setDepthLevel] = useState<number>(10); // 5단계 또는 10단계
  const [realTimeDep, setRealTimeDep] = useState<RealTimeDep | null>(null);
  const [signalRConnected, setSignalRConnected] = useState<boolean>(false);
  const [showAskingPrice, setShowAskingPrice] = useState<boolean>(false); // 호가/예상체결 정보 표시 여부
  const [signalRError, setSignalRError] = useState<string | null>(null);
  const [serverBaseURL, setServerBaseURL] = useState<string>('');
  const [pingPongMessages, setPingPongMessages] = useState<Array<{datetime: string, message: string}>>([]);
  const [depMessages, setDepMessages] = useState<Array<{datetime: string, message: string, data: any}>>([]);
  const [priceMessages, setPriceMessages] = useState<Array<{datetime: string, message: string, data: any}>>([]);
  const hubConnectionRef = useRef<signalR.HubConnection | null>(null);
  const { msgBox: m, logger } = useMessageState();
  const axiosInstance = getMainAxios();

  // 실시간 가격 조회 함수
  const fetchRealTimePrice = async (targetProdId?: string) => {
    const searchProdId = targetProdId || prodId;
    if (!searchProdId) {
      setRealTimePrice(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get('/api/v1/stocks/realtime-price', {
        params: { prodId: searchProdId }
      });
      
      if (response.data) {
        setRealTimePrice(response.data);
      } else {
        setRealTimePrice(null);
        setError(`종목 ${searchProdId}의 실시간 가격을 찾을 수 없습니다.`);
      }
    } catch (error: any) {
      console.error('실시간 가격 조회 실패:', error);
      // 404 오류인 경우 실시간 가격이 아직 수신되지 않았을 수 있으므로 경고만 표시
      if (error?.response?.status === 404) {
        setError(null); // 에러를 표시하지 않음 (SignalR로 실시간 업데이트될 예정)
        console.log('실시간 가격이 아직 수신되지 않았습니다. SignalR 연결을 통해 업데이트될 예정입니다.');
      } else {
        setError(error?.response?.data?.error || '실시간 가격 조회에 실패했습니다.');
      }
      setRealTimePrice(null);
    } finally {
      setLoading(false);
    }
  };

  // 전체 실시간 가격 조회
  const fetchAllPrices = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get('/api/v1/stocks/realtime-price');
      
      if (response.data && typeof response.data === 'object') {
        setAllPrices(response.data);
      } else {
        setAllPrices({});
      }
    } catch (error: any) {
      console.error('전체 실시간 가격 조회 실패:', error);
      setError('전체 실시간 가격 조회에 실패했습니다.');
      setAllPrices({});
    } finally {
      setLoading(false);
    }
  };

  // 자동 새로고침
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (autoRefresh) {
      intervalId = setInterval(() => {
        if (prodId) {
          fetchRealTimePrice();
        } else {
          fetchAllPrices();
        }
      }, 3000); // 3초마다 새로고침
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefresh, prodId]);

  // 실시간 가격 수신 시작
  const startRealTimePrice = async (prodIds?: string[]) => {
    setLoading(true);
    setError(null);

    try {
      const targetProdIds = prodIds || (prodId ? [prodId] : ['005930', '086520']);
      const response = await axiosInstance.post('/api/v1/stocks/realtime-price/start', {
        prodIds: targetProdIds
      });
      
      if (response.data && response.data.success) {
        setIsReceiving(true);
        setReceivingProdIds(targetProdIds);
        setError(null);
        // 수신 시작 후 잠시 대기 후 데이터 조회
        setTimeout(() => {
          if (targetProdIds.length === 1) {
            fetchRealTimePrice(targetProdIds[0]);
          } else {
            fetchAllPrices();
          }
        }, 2000);
      } else {
        setError('실시간 가격 수신 시작에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('실시간 가격 수신 시작 실패:', error);
      setError(error?.response?.data?.error || '실시간 가격 수신 시작에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 실시간 가격 수신 중지
  const stopRealTimePrice = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.post('/api/v1/stocks/realtime-price/stop');
      
      if (response.data && response.data.success) {
        setIsReceiving(false);
        setReceivingProdIds([]);
        setError(null);
      } else {
        setError('실시간 가격 수신 중지에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('실시간 가격 수신 중지 실패:', error);
      setError(error?.response?.data?.error || '실시간 가격 수신 중지에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 분봉 차트 조회 함수
  const fetchMinuteChart = async (targetProdId?: string, minute?: number) => {
    const searchProdId = targetProdId || prodId;
    const interval = minute || minuteInterval;

    if (!searchProdId) {
      setMinuteChartData([]);
      return;
    }

    setChartLoading(true);
    try {
      // kwchartmin DB에서 최근 5영업일 분봉 조회
      const today = new Date();
      const from = new Date(today);
      from.setDate(from.getDate() - 10); // 영업일 보정용 여유
      const fromStr = from.toISOString().split('T')[0];
      const toStr = today.toISOString().split('T')[0];

      const response = await axiosInstance.get('/api/v1/quant/intraday-bars', {
        params: { prodId: searchProdId, from: fromStr, to: toStr, minute: interval }
      });

      if (Array.isArray(response.data) && response.data.length > 0) {
        const mapped = response.data.map((b: any) => ({
          date: b.d,
          time: b.t,
          open: Number(b.o),
          high: Number(b.h),
          low: Number(b.l),
          close: Number(b.c),
          volume: Number(b.v),
          acmlVolume: 0,
        }));
        setMinuteChartData(mapped);
      } else {
        setMinuteChartData([]);
      }
    } catch (error: any) {
      console.error('분봉 차트 조회 실패:', error);
      setMinuteChartData([]);
    } finally {
      setChartLoading(false);
    }
  };

  const fetchDailyChart = async (targetProdId?: string) => {
    const searchProdId = targetProdId || prodId;
    if (!searchProdId) { setDailyChartData([]); return; }
    setChartLoading(true);
    try {
      const response = await axiosInstance.get('/api/v1/quant/screener/history', { params: { prodId: searchProdId } });
      if (Array.isArray(response.data)) {
        setDailyChartData(response.data);
      } else {
        setDailyChartData([]);
      }
    } catch (error) {
      console.error('일봉 차트 조회 실패:', error);
      setDailyChartData([]);
    } finally {
      setChartLoading(false);
    }
  };

  // prodId가 변경되면 분봉 차트 조회 (실시간 가격과 무관)
  useEffect(() => {
    if (prodId && prodId.trim()) {
      console.log('종목코드 변경 감지, 분봉 차트 조회:', prodId);
      fetchMinuteChart(prodId, minuteInterval);
    }
  }, [prodId, minuteInterval]);

  // SignalR 연결 설정
  useEffect(() => {
    // API baseURL 가져오기
    const apiConfig = getApiConfig();
    const baseURL = apiConfig.BASE_URL;
    const signalRUrl = `${baseURL}/hubs/realtime-price`;
    
    setServerBaseURL(baseURL);
    console.log('=== SignalR 연결 시도 ===');
    console.log('API Base URL:', baseURL);
    console.log('SignalR 연결 URL:', signalRUrl);
    console.log('현재 시간:', new Date().toLocaleString('ko-KR'));
    
    // 서버 연결 확인
    fetch(`${baseURL}/api/v1/HealthCheck`)
      .then(res => {
        console.log('서버 HealthCheck 응답:', res.status);
        if (res.ok) {
          setSignalRError(null);
        } else {
          console.warn('서버 HealthCheck 실패:', res.status);
          setSignalRError(`서버 HealthCheck 실패: ${res.status}`);
        }
      })
      .catch(err => {
        console.error('서버 연결 확인 실패:', err);
        setSignalRError(`서버 연결 불가: ${baseURL}. 서버가 실행 중인지 확인하세요.`);
      });
    
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(signalRUrl, { accessTokenFactory: () => localStorage.getItem('fds_token') ?? '' })
      .withAutomaticReconnect([3000, 6000, 9000, 12000, 15000])
      .build();

    // 실시간 가격 업데이트 수신 - 연결 전에 핸들러 등록
    console.log('[클라이언트] PriceUpdated 핸들러 등록 시작');
    connection.on('PriceUpdated', (price: RealTimePrice) => {
      console.log('=== 체결 데이터 수신됨 ===');
      console.log('수신된 체결 데이터:', price);
      console.log('데이터 타입:', typeof price);
      console.log('종목 코드:', price?.prodId);
      console.log('현재가:', price?.currentPrice);
      
      const timestamp = new Date().toLocaleString('ko-KR');
      const message = `체결: ${price.prodId} | 현재가: ${price.currentPrice.toLocaleString()}원 | 거래량: ${price.volume.toLocaleString()}`;
      
      setPriceMessages(prev => {
        const newMessages = [...prev, { 
          datetime: timestamp, 
          message, 
          data: price 
        }];
        return newMessages.slice(-50); // 최근 50개만 유지
      });
      
      setRealTimePrice(price);
      setAllPrices(prev => ({
        ...prev,
        [price.prodId]: price
      }));
    });
    console.log('[클라이언트] PriceUpdated 핸들러 등록 완료');

    // 실시간 호가 업데이트 수신 - 연결 전에 핸들러 등록
    console.log('[클라이언트] DepUpdated 핸들러 등록 시작');
    connection.on('DepUpdated', (dep: RealTimeDep) => {
      console.log('=== 실시간 호가 데이터 수신됨 ===');
      console.log('수신된 호가 데이터:', dep);
      console.log('데이터 타입:', typeof dep);
      console.log('종목 코드:', dep?.prodId);
      console.log('시간:', dep?.bsopHour);
      
      const timestamp = new Date().toLocaleString('ko-KR');
      const message = `호가: ${dep.prodId} | 시간: ${dep.bsopHour} | 예상가: ${dep.antcCnpr?.toLocaleString() || 0}원`;
      
      setDepMessages(prev => {
        const newMessages = [...prev, { 
          datetime: timestamp, 
          message, 
          data: dep 
        }];
        return newMessages.slice(-50); // 최근 50개만 유지
      });
      
      setRealTimeDep(dep);
      
      // 현재 선택된 종목의 호가인 경우 askingPriceData 업데이트
      if (dep.prodId === prodId || !prodId) {
        console.log('호가 데이터 매핑 시작:', dep.prodId, dep.bsopHour);
        console.log('매도 호가 배열:', dep.askPrices);
        console.log('매도 잔량 배열:', dep.askQuantities);
        console.log('매도 호가 배열 길이:', dep.askPrices?.length);
        console.log('매도 잔량 배열 길이:', dep.askQuantities?.length);
        try {
          // 배열이 없거나 길이가 부족한 경우 기본값으로 채우기
          const askPricesArray = dep.askPrices || [];
          const askQuantitiesArray = dep.askQuantities || [];
          const bidPricesArray = dep.bidPrices || [];
          const bidQuantitiesArray = dep.bidQuantities || [];
          
          // 10개로 고정 (부족하면 0으로 채움)
          const askPrices = Array.from({ length: 10 }, (_, i) => 
            askPricesArray[i]?.toString() || '0'
          );
          const askQuantities = Array.from({ length: 10 }, (_, i) => 
            askQuantitiesArray[i]?.toString() || '0'
          );
          const bidPrices = Array.from({ length: 10 }, (_, i) => 
            bidPricesArray[i]?.toString() || '0'
          );
          const bidQuantities = Array.from({ length: 10 }, (_, i) => 
            bidQuantitiesArray[i]?.toString() || '0'
          );
          
          console.log('매핑된 매도 호가:', askPrices);
          console.log('매핑된 매도 잔량:', askQuantities);
          
          setAskingPriceData({
            askingPrice: {
              asprAcptHour: dep.bsopHour,
              askPrices: askPrices,
              bidPrices: bidPrices,
              askQuantities: askQuantities,
              bidQuantities: bidQuantities,
              totalAskQuantity: dep.totalAskQuantity?.toString() || '0',
              totalBidQuantity: dep.totalBidQuantity?.toString() || '0'
            },
            expectedPrice: {
              currentPrice: 0,
              openPrice: 0,
              highPrice: 0,
              lowPrice: 0,
              standardPrice: 0,
              expectedPrice: dep.antcCnpr,
              expectedPriceChangeSign: dep.antcCntgVrssSign,
              expectedPriceChange: dep.antcCntgVrss.toString(),
              expectedPriceChangeRate: dep.antcCntgPrdyCtrt.toString(),
              expectedVolume: dep.antcVol
            }
          });
          console.log('호가 데이터 매핑 완료');
        } catch (error) {
          console.error('호가 데이터 매핑 오류:', error);
        }
      } else {
        console.log('호가 데이터 매핑 건너뜀 - 종목 불일치');
      }
    });

    // PINGPONG 메시지 수신
    console.log('PINGPONG 핸들러 등록 시작');
    connection.on('PingPong', (data: any) => {
      console.log('=== PINGPONG 이벤트 수신됨 ===');
      console.log('데이터:', data);
      console.log('데이터 타입:', typeof data);
      console.log('데이터 문자열화:', JSON.stringify(data));
      try {
        let message = '';
        let datetime = '';
        let rawData = data;
        
        // 문자열인 경우 파싱
        if (typeof data === 'string') {
          try {
            rawData = JSON.parse(data);
          } catch (e) {
            // 이미 파싱된 경우
            rawData = data;
          }
        }
        
        // 파싱된 데이터에서 정보 추출
        if (rawData && typeof rawData === 'object') {
          if (rawData.header) {
            datetime = rawData.header.datetime || '';
            const trId = rawData.header.tr_id || '';
            if (trId === 'PINGPONG') {
              message = `PINGPONG 수신: ${datetime}`;
            } else {
              message = `메시지 수신: ${trId} - ${datetime || JSON.stringify(rawData)}`;
            }
          } else {
            message = `PINGPONG: ${JSON.stringify(rawData)}`;
          }
        } else {
          message = `PINGPONG: ${String(data)}`;
        }
        
        const timestamp = new Date().toLocaleString('ko-KR');
        const displayMessage = `${timestamp} - ${message}`;
        
        console.log('PINGPONG 메시지 처리:', displayMessage);
        
        setPingPongMessages(prev => {
          const newMessages = [...prev, { datetime, message: displayMessage }];
          // 최근 50개만 유지
          return newMessages.slice(-50);
        });
      } catch (error) {
        console.error('PINGPONG 메시지 파싱 오류:', error, data);
        const timestamp = new Date().toLocaleString('ko-KR');
        setPingPongMessages(prev => {
          const newMessages = [...prev, { datetime: '', message: `${timestamp} - PINGPONG (파싱 오류): ${JSON.stringify(data)}` }];
          return newMessages.slice(-50);
        });
      }
    });
    
    // 모든 이벤트를 로깅하기 위한 디버깅 핸들러
    connection.onreconnecting((error) => {
      console.log('SignalR 재연결 중...', error);
      setSignalRConnected(false);
    });
    
    connection.onreconnected((connectionId) => {
      console.log('SignalR 재연결 성공:', connectionId);
      setSignalRConnected(true);
    });

    // 연결 시작
    connection.start()
      .then(() => {
        console.log('=== SignalR 연결 성공 ===');
        console.log('연결 ID:', connection.connectionId);
        console.log('연결 상태:', connection.state);
        setSignalRConnected(true);
        
        // 연결 후 이벤트 핸들러가 등록되었는지 확인
        console.log('=== SignalR 연결 성공 ===');
        console.log('연결 상태:', connection.state);
        console.log('연결 ID:', connection.connectionId);
        console.log('등록된 이벤트 핸들러:', {
          PriceUpdated: true,
          DepUpdated: true,
          PingPong: true
        });
        
        // 모든 이벤트를 감지하기 위한 디버깅
        connection.onclose((error) => {
          console.error('=== SignalR 연결 종료 ===', error);
        });
        
        console.log('SignalR 연결 완료 - 실시간 호가, 체결, PINGPONG 메시지 대기 중...');
      })
      .catch((err) => {
        console.error('=== SignalR 연결 실패 ===');
        console.error('오류 상세:', err);
        console.error('연결 URL:', signalRUrl);
        console.error('서버 Base URL:', baseURL);
        setSignalRConnected(false);
        setSignalRError(`서버 연결 실패: ${baseURL}. 서버가 실행 중인지 확인하세요.`);
        
        // 서버 연결 확인
        fetch(`${baseURL}/api/v1/HealthCheck`)
          .then(res => {
            console.log('서버 HealthCheck 응답:', res.status, res.statusText);
            if (res.ok) {
              console.log('서버는 실행 중입니다. SignalR 연결을 다시 시도합니다...');
              setSignalRError(null);
              // 3초 후 재연결 시도
              setTimeout(() => {
                console.log('SignalR 재연결 시도...');
                connection.start()
                  .then(() => {
                    console.log('SignalR 재연결 성공');
                    setSignalRConnected(true);
                    setSignalRError(null);
                  })
                  .catch((retryErr) => {
                    console.error('SignalR 재연결 실패:', retryErr);
                    setSignalRError(`재연결 실패: ${retryErr.message}`);
                  });
              }, 3000);
            } else {
              console.error('서버 HealthCheck 실패:', res.status);
              setSignalRError(`서버 HealthCheck 실패: ${res.status}`);
            }
          })
          .catch(healthErr => {
            console.error('서버 연결 확인 실패:', healthErr);
            setSignalRError(`서버 연결 불가: ${baseURL}. 서버가 실행 중인지 확인하세요.`);
          });
      });

    // 연결 상태 변경 감지
    connection.onreconnecting(() => {
      console.log('SignalR 재연결 중...');
      setSignalRConnected(false);
    });

    connection.onreconnected(() => {
      console.log('SignalR 재연결 성공');
      setSignalRConnected(true);
    });

    connection.onclose(() => {
      console.log('SignalR 연결 종료');
      setSignalRConnected(false);
    });

    hubConnectionRef.current = connection;

    // 컴포넌트 언마운트 시 연결 종료
    return () => {
      if (connection) {
        connection.stop();
      }
    };
  }, []);

  // 초기 로드 시 전체 가격 조회
  useEffect(() => {
    fetchAllPrices();
  }, []);

  const handleSearch = () => {
    if (prodId.trim()) {
      fetchRealTimePrice();
      // 실시간 가격과 무관하게 분봉 차트도 조회
      fetchMinuteChart(prodId, minuteInterval);
    } else {
      fetchAllPrices();
      setMinuteChartData([]); // 종목코드가 없으면 차트 데이터 초기화
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // 차트 데이터 빌드
  const buildChartData = () => {
    if (chartType === 'minute') {
      const candles = minuteChartData.map(m => {
        const ts = krxBarToEpochMs(m.date, m.time);
        return [ts, m.open, m.high, m.low, m.close];
      }).sort((a, b) => (a[0] as number) - (b[0] as number));
      const vols = minuteChartData.map(m => {
        const ts = krxBarToEpochMs(m.date, m.time);
        return [ts, m.volume];
      }).sort((a, b) => (a[0] as number) - (b[0] as number));
      return { candles, vols };
    }

    // 일봉 데이터
    const dailyFiltered = dailyChartData.filter((d: any) => d.c > 0 && d.h > 0);
    if (chartType === 'weekly') {
      // 주봉: 주 단위 OHLCV 집계 (월요일 기준)
      const weekMap = new Map<string, any[]>();
      dailyFiltered.forEach((d: any) => {
        const dt = new Date(d.date);
        const day = dt.getDay();
        const monday = new Date(dt);
        monday.setDate(dt.getDate() - ((day + 6) % 7));
        const key = monday.toISOString().substring(0, 10);
        if (!weekMap.has(key)) weekMap.set(key, []);
        weekMap.get(key)!.push(d);
      });
      const candles: any[] = [];
      const vols: any[] = [];
      weekMap.forEach((bars, key) => {
        const ts = new Date(key).getTime();
        candles.push([ts, bars[0].o, Math.max(...bars.map((b: any) => b.h)), Math.min(...bars.map((b: any) => b.l)), bars[bars.length - 1].c]);
        vols.push([ts, bars.reduce((s: number, b: any) => s + b.v, 0)]);
      });
      candles.sort((a, b) => a[0] - b[0]);
      vols.sort((a, b) => a[0] - b[0]);
      return { candles, vols };
    }

    // 일봉
    const candles = dailyFiltered.map((d: any) => [new Date(d.date).getTime(), d.o, d.h, d.l, d.c]).sort((a: any, b: any) => a[0] - b[0]);
    const vols = dailyFiltered.map((d: any) => [new Date(d.date).getTime(), d.v]).sort((a: any, b: any) => a[0] - b[0]);
    return { candles, vols };
  };

  const { candles: chartCandles, vols: chartVols } = buildChartData();

  const chartTypeLabel = chartType === 'minute' ? `${minuteInterval}분봉` : chartType === 'daily' ? '일봉' : '주봉';
  const chartStyleLabel = priceSeriesType === 'line' ? '라인(종가)' : '캔들';

  const linePriceData: [number, number][] =
    chartCandles.length > 0
      ? (chartCandles as number[][]).map((c) => [c[0], c[4]])
      : [];

  const minuteBarStepMs = minuteInterval * 60 * 1000;
  const intradayXAxisBreaks =
    chartType === 'minute' && chartCandles.length > 1
      ? buildIntradayAxisBreaks(
          (chartCandles as number[][]).map((c) => c[0]),
          minuteBarStepMs
        )
      : undefined;

  const priceYMin =
    chartCandles.length > 0
      ? (priceSeriesType === 'line'
        ? Math.min(...(chartCandles as number[][]).map((c) => c[4]))
        : Math.min(...(chartCandles as number[][]).map((c) => c[3]))) * 0.98
      : undefined;

  // Highcharts Stock: candlestick + volume 또는 line(종가) + volume
  const chartOptions: Highcharts.Options = {
    time: { timezone: 'Asia/Seoul' },
    title: {
      text: prodId ? `${prodId} - ${chartTypeLabel} · ${chartStyleLabel}` : '차트',
      style: { fontSize: '14px' },
    },
    plotOptions: {
      series: { dataGrouping: { enabled: false } },
      candlestick: { color: '#ef4444', lineColor: '#ef4444', upColor: '#22c55e', upLineColor: '#22c55e' },
      line: { lineWidth: 2, marker: { enabled: chartCandles.length <= 120, radius: 2 } },
    },
    xAxis: {
      type: 'datetime',
      breaks: intradayXAxisBreaks,
      labels: { style: { fontSize: '11px' } },
    },
    yAxis: [{
      labels: { align: 'right', x: -3 },
      title: { text: priceSeriesType === 'line' ? '종가' : 'OHLC' },
      height: '60%',
      lineWidth: 1,
      min: priceYMin,
    }, {
      labels: { align: 'right', x: -3 },
      title: { text: 'Volume' },
      top: '65%',
      height: '35%',
      offset: 0,
      lineWidth: 1,
    }],
    series:
      priceSeriesType === 'line'
        ? ([
            {
              type: 'line',
              name: '종가',
              data: linePriceData,
              color: '#2563eb',
              tooltip: { valueDecimals: 0 },
            } as Highcharts.SeriesLineOptions,
            {
              type: 'column',
              name: '거래량',
              data: chartVols,
              yAxis: 1,
            } as Highcharts.SeriesColumnOptions,
          ] as Highcharts.SeriesOptionsType[])
        : ([
            {
              type: 'candlestick',
              name: '가격',
              data: chartCandles,
            } as Highcharts.SeriesCandlestickOptions,
            {
              type: 'column',
              name: '거래량',
              data: chartVols,
              yAxis: 1,
            } as Highcharts.SeriesColumnOptions,
          ] as Highcharts.SeriesOptionsType[]),
    rangeSelector: { enabled: false },
    navigator: { enabled: true },
    scrollbar: { enabled: true },
    credits: { enabled: false },
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '10px'
      }}>
        <h2 style={{ margin: '0 0 10px 0', color: '#1e40af', fontSize: '12px', fontWeight: '600' }}>
          실시간 현재가 조회
        </h2>

        {/* 검색 영역 */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <input
              type="text"
              value={prodId}
              onChange={(e) => setProdId(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="종목코드 입력 (예: 005930)"
              style={{
                width: '100%',
                padding: '5px 8px',
                border: '1px solid #d1d5db',
                borderRadius: '3px',
                fontSize: '12px',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <button
            onClick={async () => {
              const targetProdId = prodId || '005930';
              setLoading(true);
              setError(null);
              try {
                const response = await axiosInstance.get(`/api/v1/stocks/asking-price-exp-ccn/${targetProdId}`);
                if (response.data && response.data.success) {
                  setAskingPriceData(response.data);
                } else {
                  setError('호가/예상체결 조회에 실패했습니다.');
                }
              } catch (error: any) {
                console.error('호가/예상체결 조회 실패:', error);
                setError(error?.response?.data?.error || '호가/예상체결 조회에 실패했습니다.');
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
            style={{
              padding: '5px 12px',
              backgroundColor: '#8b5cf6',
              color: 'white',
              border: 'none',
              borderRadius: '3px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? '조회 중...' : '호가/예상체결'}
          </button>
          <button
            onClick={async () => {
              const targetProdId = prodId || '005930';
              setLoading(true);
              setError(null);
              try {
                const response = await axiosInstance.get(`/api/v1/stocks/ccnl/${targetProdId}`);
                if (response.data && response.data.success) {
                  setCcnlData(response.data.data || []);
                } else {
                  setError('체결 데이터 조회에 실패했습니다.');
                }
              } catch (error: any) {
                console.error('체결 데이터 조회 실패:', error);
                setError(error?.response?.data?.error || '체결 데이터 조회에 실패했습니다.');
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
            style={{
              padding: '5px 12px',
              backgroundColor: '#ec4899',
              color: 'white',
              border: 'none',
              borderRadius: '3px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? '조회 중...' : '체결 데이터'}
          </button>
          {!isReceiving ? (
            <button
              onClick={() => startRealTimePrice(prodId ? [prodId] : undefined)}
              disabled={loading}
              style={{
                padding: '5px 12px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '3px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '12px',
              fontWeight: '500',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? '시작 중...' : '실시간 수신 시작'}
          </button>
        ) : (
          <button
            onClick={stopRealTimePrice}
            disabled={loading}
            style={{
              padding: '5px 12px',
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '3px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? '중지 중...' : '실시간 수신 중지'}
          </button>
        )}
        <button
          onClick={() => setShowAskingPrice(!showAskingPrice)}
          style={{
            padding: '5px 12px',
            backgroundColor: showAskingPrice ? '#22c55e' : '#6b7280',
            color: 'white',
            border: 'none',
            borderRadius: '3px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '500'
          }}
        >
          {showAskingPrice ? '호가/예상체결 숨김' : '호가/예상체결'}
        </button>
        <button
          onClick={handleSearch}
          disabled={loading}
          style={{
            padding: '5px 12px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '3px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            fontWeight: '500',
            opacity: loading ? 0.6 : 1
          }}
        >
          {loading ? '조회 중...' : '조회'}
        </button>
        <button
          onClick={() => {
            setProdId('');
            fetchAllPrices();
          }}
          disabled={loading}
          style={{
            padding: '5px 12px',
            backgroundColor: '#6b7280',
            color: 'white',
            border: 'none',
            borderRadius: '3px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '12px',
              fontWeight: '500',
              opacity: loading ? 0.6 : 1
            }}
          >
            전체 조회
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px',
              padding: '4px 8px',
              backgroundColor: signalRConnected ? '#22c55e' : '#ef4444',
              borderRadius: '4px',
              fontSize: '11px',
              color: 'white',
              fontWeight: '500'
            }}>
              <div style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: 'white'
              }} />
              {signalRConnected ? '실시간 연결됨' : '연결 끊김'}
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 8px',
              backgroundColor: pingPongMessages.length > 0 ? '#f3f4f6' : '#fee2e2',
              borderRadius: '4px',
              fontSize: '10px',
              color: '#374151',
              maxWidth: '300px',
              overflow: 'hidden'
            }} title={pingPongMessages.length > 0 ? pingPongMessages[pingPongMessages.length - 1]?.message : 'PINGPONG 메시지 없음'}>
              <span style={{ fontWeight: '500' }}>PING:</span>
              <span>{pingPongMessages.length}건</span>
              {pingPongMessages.length === 0 && signalRConnected && (
                <span style={{ fontSize: '9px', color: '#ef4444', marginLeft: '4px' }}>(대기중)</span>
              )}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                style={{ width: '9px', height: '9px', cursor: 'pointer' }}
              />
            <span style={{ fontSize: '12px', color: '#374151' }}>자동 새로고침 (3초)</span>
          </label>
          </div>
        </div>

        {/* 실시간 수신 상태 표시 */}
        {isReceiving && (
          <div style={{
            padding: '6px',
            backgroundColor: '#d1fae5',
            border: '1px solid #10b981',
            borderRadius: '3px',
            marginBottom: '10px',
            fontSize: '12px',
            color: '#065f46'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '2px', fontSize: '12px' }}>
              ✅ 실시간 가격 수신 중
            </div>
            <div style={{ fontSize: '12px' }}>
              수신 종목: {receivingProdIds.join(', ')}
            </div>
          </div>
        )}

        {/* 에러 메시지 */}
        {error && (
          <div style={{
            padding: '6px',
            backgroundColor: '#fee2e2',
            border: '1px solid #ef4444',
            borderRadius: '2px',
            color: '#991b1b',
            fontSize: '12px',
            marginBottom: '10px'
          }}>
            {error}
          </div>
        )}

        {/* SignalR 연결 상태 메시지 */}
        {signalRError && (
          <div style={{
            padding: '10px',
            backgroundColor: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: '4px',
            color: '#92400e',
            fontSize: '12px',
            marginBottom: '10px',
            fontWeight: '500'
          }}>
            ⚠️ {signalRError}
            <div style={{ fontSize: '11px', marginTop: '6px', color: '#78350f', lineHeight: '1.6' }}>
              <div>• 서버 주소: <strong>{serverBaseURL || 'http://localhost:5108'}</strong></div>
              <div>• 서버가 실행 중인지 확인하세요</div>
              <div>• 서버가 다른 포트에서 실행 중이라면, fdsapp/.env 파일에 다음을 추가하세요:</div>
              <div style={{ 
                marginTop: '4px', 
                padding: '4px 8px', 
                backgroundColor: '#fef3c7', 
                borderRadius: '2px',
                fontFamily: 'monospace',
                fontSize: '10px'
              }}>
                VITE_API_BASE_URL=http://localhost:실제포트번호
              </div>
            </div>
          </div>
        )}

        {/* SignalR 연결 성공 메시지 */}
        {signalRConnected && !signalRError && (
          <div style={{
            padding: '6px',
            backgroundColor: '#d1fae5',
            border: '1px solid #10b981',
            borderRadius: '2px',
            color: '#065f46',
            fontSize: '12px',
            marginBottom: '10px',
            fontWeight: '500'
          }}>
            ✅ SignalR 연결됨 - 실시간 데이터 수신 중
          </div>
        )}

        {/* PINGPONG 메시지 로그 */}
        <div style={{
          padding: '8px',
          backgroundColor: pingPongMessages.length > 0 ? '#f9fafb' : '#fef2f2',
          border: `1px solid ${pingPongMessages.length > 0 ? '#d1d5db' : '#fecaca'}`,
          borderRadius: '4px',
          marginBottom: '10px',
          maxHeight: '150px',
          overflowY: 'auto'
        }}>
          <div style={{ 
            fontSize: '11px', 
            fontWeight: '600', 
            color: pingPongMessages.length > 0 ? '#374151' : '#991b1b', 
            marginBottom: '6px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>PINGPONG 메시지 로그 ({pingPongMessages.length}건)</span>
            {signalRConnected && (
              <span style={{ fontSize: '9px', color: '#22c55e', fontWeight: 'normal' }}>
                SignalR 연결됨
              </span>
            )}
          </div>
          {pingPongMessages.length > 0 ? (
            <div style={{ fontSize: '10px', color: '#6b7280', lineHeight: '1.5' }}>
              {pingPongMessages.slice(-10).reverse().map((msg, idx) => (
                <div key={idx} style={{ marginBottom: '4px', fontFamily: 'monospace' }}>
                  {msg.message}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: '10px', color: '#991b1b', fontStyle: 'italic', textAlign: 'center', padding: '10px' }}>
              {signalRConnected ? 'PINGPONG 메시지 대기 중...' : 'SignalR 연결 필요'}
            </div>
          )}
        </div>

        {/* 호가 데이터 로그 */}
        <div style={{
          padding: '8px',
          backgroundColor: depMessages.length > 0 ? '#f0f9ff' : '#eff6ff',
          border: `1px solid ${depMessages.length > 0 ? '#93c5fd' : '#bfdbfe'}`,
          borderRadius: '4px',
          marginBottom: '10px',
          maxHeight: '150px',
          overflowY: 'auto'
        }}>
          <div style={{ 
            fontSize: '11px', 
            fontWeight: '600', 
            color: depMessages.length > 0 ? '#1e40af' : '#3b82f6', 
            marginBottom: '6px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>호가 데이터 로그 ({depMessages.length}건)</span>
            {signalRConnected && (
              <span style={{ fontSize: '9px', color: '#22c55e', fontWeight: 'normal' }}>
                SignalR 연결됨
              </span>
            )}
          </div>
          {depMessages.length > 0 ? (
            <div style={{ fontSize: '10px', color: '#1e3a8a', lineHeight: '1.5' }}>
              {depMessages.slice(-10).reverse().map((msg, idx) => (
                <div key={idx} style={{ marginBottom: '4px', fontFamily: 'monospace' }}>
                  {msg.message}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: '10px', color: '#3b82f6', fontStyle: 'italic', textAlign: 'center', padding: '10px' }}>
              호가 데이터 대기 중...
            </div>
          )}
        </div>

        {/* 체결 데이터 로그 */}
        <div style={{
          padding: '8px',
          backgroundColor: priceMessages.length > 0 ? '#f0fdf4' : '#f0fdf4',
          border: `1px solid ${priceMessages.length > 0 ? '#86efac' : '#bbf7d0'}`,
          borderRadius: '4px',
          marginBottom: '10px',
          maxHeight: '150px',
          overflowY: 'auto'
        }}>
          <div style={{ 
            fontSize: '11px', 
            fontWeight: '600', 
            color: priceMessages.length > 0 ? '#166534' : '#22c55e', 
            marginBottom: '6px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>체결 데이터 로그 ({priceMessages.length}건)</span>
            {signalRConnected && (
              <span style={{ fontSize: '9px', color: '#22c55e', fontWeight: 'normal' }}>
                SignalR 연결됨
              </span>
            )}
          </div>
          {priceMessages.length > 0 ? (
            <div style={{ fontSize: '10px', color: '#166534', lineHeight: '1.5' }}>
              {priceMessages.slice(-10).reverse().map((msg, idx) => (
                <div key={idx} style={{ marginBottom: '4px', fontFamily: 'monospace' }}>
                  {msg.message}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: '10px', color: '#22c55e', fontStyle: 'italic', textAlign: 'center', padding: '10px' }}>
              체결 데이터 대기 중...
            </div>
          )}
        </div>

        {/* 단일 종목 현재가 표시 */}
        {realTimePrice && (
          <div style={{
            padding: '8px',
            backgroundColor: '#f0f9ff',
            border: '1px solid #3b82f6',
            borderRadius: '4px',
            marginBottom: '10px'
          }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#1e40af', marginBottom: '6px' }}>
              {realTimePrice.prodId} - 현재가 정보
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px', fontSize: '12px' }}>
              <div style={{ padding: '4px', backgroundColor: 'white', borderRadius: '3px' }}>
                <div style={{ color: '#6b7280', marginBottom: '2px', fontSize: '12px' }}>현재가</div>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#1e40af' }}>
                  {realTimePrice.currentPrice.toLocaleString()}원
                </div>
              </div>
              <div style={{ padding: '4px', backgroundColor: 'white', borderRadius: '3px' }}>
                <div style={{ color: '#6b7280', marginBottom: '2px', fontSize: '12px' }}>시가</div>
                <div style={{ fontSize: '12px', fontWeight: '600' }}>
                  {realTimePrice.openPrice.toLocaleString()}원
                </div>
              </div>
              <div style={{ padding: '4px', backgroundColor: 'white', borderRadius: '3px' }}>
                <div style={{ color: '#6b7280', marginBottom: '2px', fontSize: '12px' }}>고가</div>
                <div style={{ fontSize: '12px', fontWeight: '600', color: '#ef4444' }}>
                  {realTimePrice.highPrice.toLocaleString()}원
                </div>
              </div>
              <div style={{ padding: '4px', backgroundColor: 'white', borderRadius: '3px' }}>
                <div style={{ color: '#6b7280', marginBottom: '2px', fontSize: '12px' }}>저가</div>
                <div style={{ fontSize: '12px', fontWeight: '600', color: '#22c55e' }}>
                  {realTimePrice.lowPrice.toLocaleString()}원
                </div>
              </div>
              <div style={{ padding: '4px', backgroundColor: 'white', borderRadius: '3px', gridColumn: 'span 2' }}>
                <div style={{ color: '#6b7280', marginBottom: '2px', fontSize: '12px' }}>거래량</div>
                <div style={{ fontSize: '12px', fontWeight: '600' }}>
                  {realTimePrice.volume.toLocaleString()}주
                </div>
              </div>
            </div>
            <div style={{ marginTop: '6px', fontSize: '12px', color: '#9ca3af', textAlign: 'right' }}>
              업데이트: {new Date(realTimePrice.updateTime).toLocaleString('ko-KR')}
            </div>
          </div>
        )}

        {/* 호가/예상체결 정보 */}
        {showAskingPrice && askingPriceData && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#1e40af', fontSize: '12px', fontWeight: '600' }}>
              호가/예상체결 정보
            </h3>
            
            {/* 예상체결 정보 */}
            {askingPriceData.expectedPrice && (
              <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f0f9ff', borderRadius: '6px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '12px', fontWeight: '600', color: '#1e40af' }}>
                  예상체결 정보
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', fontSize: '12px' }}>
                  <div>
                    <div style={{ color: '#6b7280', marginBottom: '4px', fontSize: '12px' }}>예상체결가</div>
                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#1e40af' }}>
                      {askingPriceData.expectedPrice.expectedPrice.toLocaleString()}원
                    </div>
                  </div>
                  <div>
                    <div style={{ color: '#6b7280', marginBottom: '4px', fontSize: '12px' }}>예상 대비</div>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: askingPriceData.expectedPrice.expectedPriceChangeSign === '+' ? '#ef4444' : '#22c55e' }}>
                      {askingPriceData.expectedPrice.expectedPriceChangeSign}{askingPriceData.expectedPrice.expectedPriceChange}
                      ({askingPriceData.expectedPrice.expectedPriceChangeRate}%)
                    </div>
                  </div>
                  <div>
                    <div style={{ color: '#6b7280', marginBottom: '4px', fontSize: '12px' }}>예상 거래량</div>
                    <div style={{ fontSize: '12px', fontWeight: '600' }}>
                      {askingPriceData.expectedPrice.expectedVolume.toLocaleString()}주
                    </div>
                  </div>
                  <div>
                    <div style={{ color: '#6b7280', marginBottom: '4px', fontSize: '12px' }}>현재가</div>
                    <div style={{ fontSize: '12px', fontWeight: '600' }}>
                      {askingPriceData.expectedPrice.currentPrice.toLocaleString()}원
                    </div>
                  </div>
                  <div>
                    <div style={{ color: '#6b7280', marginBottom: '4px', fontSize: '12px' }}>시가</div>
                    <div style={{ fontSize: '12px', fontWeight: '600' }}>
                      {askingPriceData.expectedPrice.openPrice.toLocaleString()}원
                    </div>
                  </div>
                  <div>
                    <div style={{ color: '#6b7280', marginBottom: '4px', fontSize: '12px' }}>기준가</div>
                    <div style={{ fontSize: '12px', fontWeight: '600' }}>
                      {askingPriceData.expectedPrice.standardPrice.toLocaleString()}원
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 호가 정보 - 전통적인 호가창 스타일 */}
            {askingPriceData.askingPrice && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#1e40af' }}>
                    호가 ({askingPriceData.askingPrice.asprAcptHour})
                  </h4>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => setDepthLevel(5)}
                      style={{
                        padding: '4px 12px',
                        backgroundColor: depthLevel === 5 ? '#3b82f6' : '#e5e7eb',
                        color: depthLevel === 5 ? 'white' : '#374151',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        fontWeight: depthLevel === 5 ? '600' : '400'
                      }}
                    >
                      5단계
                    </button>
                    <button
                      onClick={() => setDepthLevel(10)}
                      style={{
                        padding: '4px 12px',
                        backgroundColor: depthLevel === 10 ? '#3b82f6' : '#e5e7eb',
                        color: depthLevel === 10 ? 'white' : '#374151',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        fontWeight: depthLevel === 10 ? '600' : '400'
                      }}
                    >
                      10단계
                    </button>
                  </div>
                </div>
                <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '10px', textAlign: 'center', fontWeight: '600', color: '#374151', width: '25%', fontSize: '12px' }}>매도잔량</th>
                        <th style={{ padding: '10px', textAlign: 'center', fontWeight: '600', color: '#374151', width: '25%', fontSize: '12px' }}>매도호가</th>
                        <th style={{ padding: '10px', textAlign: 'center', fontWeight: '600', color: '#374151', width: '25%', fontSize: '12px' }}>매수호가</th>
                        <th style={{ padding: '10px', textAlign: 'center', fontWeight: '600', color: '#374151', width: '25%', fontSize: '12px' }}>매수잔량</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* 매도호가 먼저 표시 (10단 -> 1단) */}
                      {Array.from({ length: depthLevel }).map((_, idx) => {
                        const askIdx = depthLevel - 1 - idx; // 매도 인덱스 (9, 8, 7, ..., 0) - 위에서 아래로
                        const askPrice = askingPriceData.askingPrice.askPrices[askIdx] || '';
                        const askQty = askingPriceData.askingPrice.askQuantities[askIdx] || '0';
                        
                        // 디버깅: 첫 번째 행만 로그 출력
                        if (idx === 0) {
                          console.log('호가 표시 - 매도 인덱스:', askIdx, '가격:', askPrice, '잔량:', askQty);
                          console.log('전체 매도 호가 배열:', askingPriceData.askingPrice.askPrices);
                          console.log('전체 매도 잔량 배열:', askingPriceData.askingPrice.askQuantities);
                        }
                        
                        return (
                          <tr key={`ask-${idx}`} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ 
                              padding: '5px', 
                              textAlign: 'right', 
                              backgroundColor: askPrice ? '#fef2f2' : 'white',
                              color: '#6b7280',
                              fontWeight: askPrice ? '600' : '400',
                              fontSize: '12px'
                            }}>
                              {askPrice ? parseInt(askQty).toLocaleString() : ''}
                            </td>
                            <td style={{ 
                              padding: '5px', 
                              textAlign: 'right', 
                              backgroundColor: askPrice ? '#fef2f2' : 'white',
                              color: '#ef4444',
                              fontWeight: '600',
                              fontSize: '12px'
                            }}>
                              {askPrice ? parseInt(askPrice).toLocaleString() : ''}
                            </td>
                            <td style={{ 
                              padding: '5px', 
                              textAlign: 'right', 
                              backgroundColor: 'white',
                              color: '#6b7280',
                              fontSize: '12px'
                            }}>
                              {/* 매도 행이므로 매수 호가는 비움 */}
                            </td>
                            <td style={{ 
                              padding: '5px', 
                              textAlign: 'right', 
                              backgroundColor: 'white',
                              color: '#6b7280',
                              fontSize: '12px'
                            }}>
                              {/* 매도 행이므로 매수 잔량은 비움 */}
                            </td>
                          </tr>
                        );
                      })}
                      {/* 매수호가 표시 (1단 -> 10단) */}
                      {Array.from({ length: depthLevel }).map((_, idx) => {
                        const bidIdx = idx; // 매수 인덱스 (0, 1, 2, ..., 9) - 아래에서 위로
                        const bidPrice = askingPriceData.askingPrice.bidPrices[bidIdx] || '';
                        const bidQty = askingPriceData.askingPrice.bidQuantities[bidIdx] || '0';
                        
                        return (
                          <tr key={`bid-${idx}`} style={{ borderBottom: idx < depthLevel - 1 ? '1px solid #e5e7eb' : 'none' }}>
                            <td style={{ 
                              padding: '5px', 
                              textAlign: 'right', 
                              backgroundColor: 'white',
                              color: '#6b7280',
                              fontSize: '12px'
                            }}>
                              {/* 매수 행이므로 매도 잔량은 비움 */}
                            </td>
                            <td style={{ 
                              padding: '5px', 
                              textAlign: 'right', 
                              backgroundColor: 'white',
                              color: '#6b7280',
                              fontSize: '12px'
                            }}>
                              {/* 매수 행이므로 매도 호가는 비움 */}
                            </td>
                            <td style={{ 
                              padding: '5px', 
                              textAlign: 'right', 
                              backgroundColor: bidPrice ? '#f0fdf4' : 'white',
                              color: '#22c55e',
                              fontWeight: '600',
                              fontSize: '12px'
                            }}>
                              {bidPrice ? parseInt(bidPrice).toLocaleString() : ''}
                            </td>
                            <td style={{ 
                              padding: '5px', 
                              textAlign: 'right', 
                              backgroundColor: bidPrice ? '#f0fdf4' : 'white',
                              color: '#6b7280',
                              fontWeight: bidPrice ? '600' : '400',
                              fontSize: '12px'
                            }}>
                              {bidPrice ? parseInt(bidQty).toLocaleString() : ''}
                            </td>
                          </tr>
                        );
                      })}
                      <tr style={{ backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
                        <td colSpan={2} style={{ padding: '5px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '12px' }}>
                          잔량합계
                        </td>
                        <td colSpan={2} style={{ padding: '5px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '12px' }}>
                          {(
                            parseInt(askingPriceData.askingPrice.totalAskQuantity || '0') + 
                            parseInt(askingPriceData.askingPrice.totalBidQuantity || '0')
                          ).toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 분봉 차트 - 호가 아래로 이동 */}
        {prodId && prodId.trim() && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '4px',
            padding: '10px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginBottom: '10px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <h3 style={{ margin: 0, color: '#1e40af', fontSize: '12px', fontWeight: '600' }}>
                {chartTypeLabel} · {chartStyleLabel}
              </h3>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                {(['minute', 'daily', 'weekly'] as const).map(t => (
                  <button key={t} onClick={() => {
                    setChartType(t);
                    if (t !== 'minute' && dailyChartData.length === 0) fetchDailyChart(prodId);
                  }}
                    style={{
                      padding: '3px 10px', border: 'none', borderRadius: '3px', fontSize: '11px', cursor: 'pointer',
                      backgroundColor: chartType === t ? '#3b82f6' : '#e5e7eb',
                      color: chartType === t ? 'white' : '#374151', fontWeight: chartType === t ? 600 : 400,
                    }}>
                    {t === 'minute' ? '분봉' : t === 'daily' ? '일봉' : '주봉'}
                  </button>
                ))}
                <span style={{ color: '#d1d5db' }}>|</span>
                {(['candlestick', 'line'] as const).map((st) => (
                  <button
                    key={st}
                    type="button"
                    onClick={() => setPriceSeriesType(st)}
                    style={{
                      padding: '3px 10px',
                      border: 'none',
                      borderRadius: '3px',
                      fontSize: '11px',
                      cursor: 'pointer',
                      backgroundColor: priceSeriesType === st ? '#0d9488' : '#e5e7eb',
                      color: priceSeriesType === st ? 'white' : '#374151',
                      fontWeight: priceSeriesType === st ? 600 : 400,
                    }}
                  >
                    {st === 'candlestick' ? '캔들' : '라인'}
                  </button>
                ))}
                <span style={{ color: '#d1d5db' }}>|</span>
                {chartType === 'minute' && (
                  <>
                    <select value={minuteInterval} onChange={(e) => {
                      const v = parseInt(e.target.value); setMinuteInterval(v); fetchMinuteChart(prodId, v);
                    }} style={{ padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '12px', cursor: 'pointer' }}>
                      <option value={1}>1분</option>
                      <option value={3}>3분</option>
                      <option value={5}>5분</option>
                      <option value={10}>10분</option>
                      <option value={15}>15분</option>
                      <option value={30}>30분</option>
                      <option value={60}>60분</option>
                    </select>
                  </>
                )}
                <button onClick={() => chartType === 'minute' ? fetchMinuteChart(prodId, minuteInterval) : fetchDailyChart(prodId)}
                  disabled={chartLoading}
                  style={{ padding: '3px 6px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '3px',
                    cursor: chartLoading ? 'not-allowed' : 'pointer', fontSize: '12px', opacity: chartLoading ? 0.6 : 1 }}>
                  {chartLoading ? '조회 중...' : '새로고침'}
                </button>
              </div>
            </div>
            {chartLoading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <p style={{ color: '#1e40af', fontSize: '7px' }}>차트 로딩 중...</p>
              </div>
            ) : chartCandles.length > 0 ? (
              <div>
                <HighchartsReact
                  highcharts={Highcharts}
                  constructorType={'stockChart'}
                  options={chartOptions}
                  key={`${chartType}-${priceSeriesType}-${minuteInterval}`}
                />
                {process.env.NODE_ENV === 'development' && (
                  <div style={{ fontSize: '5px', color: '#6b7280', marginTop: '4px', textAlign: 'right' }}>
                    데이터 포인트: {minuteChartData.length}개
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                backgroundColor: '#fef3c7',
                border: '1px solid #fbbf24',
                borderRadius: '4px',
                color: '#92400e'
              }}>
                <div style={{ fontSize: '7px', marginBottom: '4px' }}>
                  분봉 데이터가 없습니다.
                </div>
                <div style={{ fontSize: '6px', color: '#92400e' }}>
                  {prodId} 종목의 당일 분봉 데이터를 조회할 수 없습니다.
                  <br />
                  장중 시간이 아니거나 데이터가 아직 생성되지 않았을 수 있습니다.
                </div>
                <button
                  onClick={() => fetchMinuteChart(prodId, minuteInterval)}
                  disabled={chartLoading}
                  style={{
                    marginTop: '6px',
                    padding: '4px 8px',
                    backgroundColor: '#f59e0b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: chartLoading ? 'not-allowed' : 'pointer',
                    fontSize: '7px'
                  }}
                >
                  다시 시도
                </button>
              </div>
            )}
          </div>
        )}

        {/* 전체 종목 현재가 표시 */}
        {!realTimePrice && Object.keys(allPrices).length > 0 && (
          <div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
              전체 실시간 가격 ({Object.keys(allPrices).length}개 종목)
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: '6px'
            }}>
              {Object.values(allPrices).map((price) => (
                <div
                  key={price.prodId}
                  onClick={() => {
                    setProdId(price.prodId);
                    setRealTimePrice(price);
                  }}
                  style={{
                    padding: '6px',
                    backgroundColor: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.backgroundColor = '#f0f9ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                  }}
                >
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#1e40af', marginBottom: '4px' }}>
                    {price.prodId}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                    <span style={{ color: '#6b7280', fontSize: '12px' }}>현재가:</span>
                    <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#1e40af' }}>
                      {price.currentPrice.toLocaleString()}원
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280' }}>
                    <span>고가: <span style={{ color: '#ef4444' }}>{price.highPrice.toLocaleString()}</span></span>
                    <span>저가: <span style={{ color: '#22c55e' }}>{price.lowPrice.toLocaleString()}</span></span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                    {new Date(price.updateTime).toLocaleTimeString('ko-KR')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 체결 데이터 */}
        {ccnlData.length > 0 && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#1e40af', fontSize: '12px', fontWeight: '600' }}>
              체결 데이터 ({ccnlData.length}건)
            </h3>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>시간</th>
                    <th style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>체결가</th>
                    <th style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>전일대비</th>
                    <th style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>체결량</th>
                    <th style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>체결강도</th>
                  </tr>
                </thead>
                <tbody>
                  {ccnlData.slice(0, 30).map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '10px', color: '#6b7280' }}>{item.time}</td>
                      <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: '#1e40af' }}>
                        {item.currentPrice.toLocaleString()}원
                      </td>
                      <td style={{ 
                        padding: '10px', 
                        textAlign: 'right', 
                        fontWeight: '600',
                        color: item.prdyVrssSign === '+' ? '#ef4444' : item.prdyVrssSign === '-' ? '#22c55e' : '#6b7280'
                      }}>
                        {item.prdyVrssSign}{item.prdyVrss} ({item.prdyCtrt}%)
                      </td>
                      <td style={{ padding: '10px', textAlign: 'right', color: '#6b7280' }}>
                        {item.cntgVol.toLocaleString()}주
                      </td>
                      <td style={{ padding: '10px', textAlign: 'right', color: '#6b7280' }}>
                        {item.tdayRltv}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 데이터 없음 메시지 */}
        {!realTimePrice && Object.keys(allPrices).length === 0 && !loading && (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            backgroundColor: '#fef3c7',
            border: '2px solid #fbbf24',
            borderRadius: '8px',
            color: '#92400e'
          }}>
            <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
              실시간 가격 정보가 없습니다
            </div>
            <div style={{ fontSize: '14px' }}>
              실시간 가격 서비스가 실행 중이 아닐 수 있습니다.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrentPrice;

